import { Router } from "express";
import { z } from "zod";
import { and, asc, count, desc, eq, gte, inArray, like, ne, or, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { users } from "../../db/schema/identity";
import { employeeDetails } from "../../db/schema/employee";
import { teams, designations, emergencyContacts, skills, employeeSkills } from "../../db/schema/hr";
import { roles, roleUser } from "../../db/schema/rbac";
import { leaves, leaveTypes, employeeLeaveQuotas } from "../../db/schema/leave";
import { attendances } from "../../db/schema/attendance";
import { projectTimeLogs } from "../../db/schema/projects";
import { companyAddresses } from "../../db/schema/tenancy";
import { auth } from "../../auth/index";
import {
  requireAuth, requirePermission, scopeCondition, SCOPE, syncUserPermissions,
} from "../../rbac/index";

/**
 * B2 Employees API — directory list + KPIs + CSV export + bulk archive.
 *
 * Ported line-by-line from legacy V2 EmployeeController (index L33–240, export L576–619,
 * bulk L557–571) — see LEGACY-PORTING-LOG.md §B2.1. "Employee" = a user holding the `employee`
 * role (company-scoped). Every query is company-scoped (our hard rule; legacy leans on the global
 * CompanyScope) and filtered by the caller's `view_employees` scope (HR ladder = ALL vs OWNED self).
 */
export const employeesRouter = Router();
employeesRouter.use(requireAuth);

const PER_PAGE = [10, 25, 50, 100] as const;
const SORTS = ["name", "email", "joined", "status", "designation", "department"] as const;

const listQuery = z.object({
  search: z.string().trim().default(""),
  department_id: z.coerce.number().int().nonnegative().default(0),
  status: z.enum(["all", "active", "inactive"]).catch("all"),
  sort: z.enum(SORTS).catch("name"),
  dir: z.enum(["asc", "desc"]).catch("asc"),
  per_page: z.coerce.number().int().catch(25),
  page: z.coerce.number().int().min(1).catch(1),
});

/** The employee-role + company predicate shared by list / summary / export, plus the scope filter. */
function baseConditions(companyId: number, userId: number, scope: number): SQL[] {
  const conds: SQL[] = [eq(users.companyId, companyId)];
  const sc = scopeCondition(scope as 1 | 2 | 3 | 4 | 5, userId, {
    addedBy: employeeDetails.addedBy,
    owner: users.id,
  });
  if (sc === "DENY") conds.push(sql`1 = 0`);
  else if (sc) conds.push(sc);
  return conds;
}

/** Compose the FROM + employee-role joins (used by list/export; count reuses the same shape). */
function fromEmployees(companyId: number) {
  return db
    .select({
      id: users.id,
      name: users.name,
      email: users.email,
      image: users.image,
      status: users.status,
      createdAt: users.createdAt,
      employeeCode: employeeDetails.employeeId,
      departmentId: employeeDetails.departmentId,
      designationId: employeeDetails.designationId,
      departmentName: teams.teamName,
      designationName: designations.name,
      dob: employeeDetails.dateOfBirth,
      joiningDate: employeeDetails.joiningDate,
      lastDate: employeeDetails.lastDate,
    })
    .from(users)
    .innerJoin(roleUser, eq(roleUser.userId, users.id))
    .innerJoin(
      roles,
      and(eq(roles.id, roleUser.roleId), eq(roles.name, "employee"), eq(roles.companyId, companyId)),
    )
    .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .leftJoin(designations, eq(designations.id, employeeDetails.designationId))
    .leftJoin(teams, eq(teams.id, employeeDetails.departmentId));
}

/** Two-digit MM-DD for birthday/anniversary matching (server date — tz refinement is a skin-pass concern). */
function mmdd(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** GET /api/employees — server-side list (rows + per-row badge map + pagination meta). */
employeesRouter.get("/employees", requirePermission("view_employees"), async (req, res) => {
  const { companyId, userId } = { companyId: req.auth!.companyId, userId: req.auth!.userId };
  const f = listQuery.parse(req.query);
  const perPage = (PER_PAGE as readonly number[]).includes(f.per_page) ? f.per_page : 25;

  const conds = baseConditions(companyId, userId, req.scope!);
  if (f.search) {
    const t = `%${f.search}%`;
    conds.push(or(like(users.name, t), like(users.email, t))!);
  }
  if (f.department_id > 0) conds.push(eq(employeeDetails.departmentId, f.department_id));
  if (f.status === "active") conds.push(eq(users.status, "active"));
  else if (f.status === "inactive") conds.push(eq(users.status, "deactive"));
  const where = and(...conds);

  // Sort (designation/department use NULLS-LAST emulation, matching legacy L102).
  const sortCol = {
    name: users.name,
    email: users.email,
    joined: users.createdAt,
    status: users.status,
    designation: designations.name,
    department: teams.teamName,
  }[f.sort];
  const dirFn = f.dir === "desc" ? desc : asc;
  const orderBy: SQL[] =
    f.sort === "designation" || f.sort === "department"
      ? [sql`${sortCol} is null asc`, dirFn(sortCol)]
      : [dirFn(sortCol)];

  const [rows, totalRow] = await Promise.all([
    fromEmployees(companyId)
      .where(where)
      .orderBy(...orderBy)
      .limit(perPage)
      .offset((f.page - 1) * perPage),
    db
      .select({ c: count() })
      .from(users)
      .innerJoin(roleUser, eq(roleUser.userId, users.id))
      .innerJoin(
        roles,
        and(eq(roles.id, roleUser.roleId), eq(roles.name, "employee"), eq(roles.companyId, companyId)),
      )
      .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
      .leftJoin(designations, eq(designations.id, employeeDetails.designationId))
      .leftJoin(teams, eq(teams.id, employeeDetails.departmentId))
      .where(where),
  ]);
  const total = Number(totalRow[0]?.c ?? 0);

  // Per-row badges (birthday / anniversary within 7d, on-leave-today) — bulk-loaded, no N+1 (legacy L109–149).
  const today = new Date();
  const weekAhead = new Map<string, number>();
  for (let i = 0; i <= 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (!weekAhead.has(mmdd(d))) weekAhead.set(mmdd(d), i);
  }
  const badges: Record<number, { birthday: number | null; anniversary: { daysUntil: number; years: number } | null; onLeaveToday: boolean }> = {};
  for (const r of rows) {
    const entry = { birthday: null as number | null, anniversary: null as { daysUntil: number; years: number } | null, onLeaveToday: false };
    if (r.dob) {
      const dob = new Date(r.dob as unknown as string);
      const du = weekAhead.get(mmdd(dob));
      if (du !== undefined) entry.birthday = du;
    }
    if (r.joiningDate) {
      const jd = new Date(r.joiningDate as unknown as string);
      const du = weekAhead.get(mmdd(jd));
      if (du !== undefined) {
        const years = today.getFullYear() - jd.getFullYear();
        if (years >= 1) entry.anniversary = { daysUntil: du, years };
      }
    }
    badges[r.id] = entry;
  }
  const pageIds = rows.map((r) => r.id);
  if (pageIds.length) {
    const todayStr = `${today.getFullYear()}-${mmdd(today)}`;
    const onLeave = await db
      .selectDistinct({ userId: leaves.userId })
      .from(leaves)
      .where(
        and(
          inArray(leaves.userId, pageIds),
          eq(leaves.status, "approved"),
          sql`${leaves.leaveDate} = ${todayStr}`,
        ),
      );
    for (const l of onLeave) {
      const b = badges[l.userId];
      if (b) b.onLeaveToday = true;
    }
  }

  res.json({
    rows,
    badges,
    pagination: { page: f.page, perPage, total, pages: Math.max(1, Math.ceil(total / perPage)) },
    filters: { ...f, per_page: perPage },
  });
});

/** GET /api/employees/summary — KPI block (legacy L151–160), scope-aware + company-scoped. */
employeesRouter.get("/employees/summary", requirePermission("view_employees"), async (req, res) => {
  const { companyId, userId } = { companyId: req.auth!.companyId, userId: req.auth!.userId };
  const base = baseConditions(companyId, userId, req.scope!);
  const join = (extra?: SQL) =>
    db
      .select({ c: count() })
      .from(users)
      .innerJoin(roleUser, eq(roleUser.userId, users.id))
      .innerJoin(
        roles,
        and(eq(roles.id, roleUser.roleId), eq(roles.name, "employee"), eq(roles.companyId, companyId)),
      )
      .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
      .where(and(...base, ...(extra ? [extra] : [])));

  const now = new Date();
  const d30ago = new Date(now);
  d30ago.setDate(now.getDate() - 30);
  const d30fwd = new Date(now);
  d30fwd.setDate(now.getDate() + 30);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [total, active, new30, exit30] = await Promise.all([
    join(),
    join(eq(users.status, "active")),
    join(gte(users.createdAt, d30ago)),
    join(sql`${employeeDetails.lastDate} >= ${iso(now)} and ${employeeDetails.lastDate} <= ${iso(d30fwd)}`),
  ]);
  res.json({
    total: Number(total[0]?.c ?? 0),
    active: Number(active[0]?.c ?? 0),
    new_30d: Number(new30[0]?.c ?? 0),
    exit_30d: Number(exit30[0]?.c ?? 0),
  });
});

/** GET /api/employees/departments — filter options (legacy L164). */
employeesRouter.get("/employees/departments", requirePermission("view_employees"), async (req, res) => {
  const rows = await db
    .select({ id: teams.id, name: teams.teamName })
    .from(teams)
    .where(eq(teams.companyId, req.auth!.companyId))
    .orderBy(asc(teams.teamName))
    .limit(100);
  res.json({ departments: rows });
});

/** GET /api/employees/options — New/Edit drawer pickers + next employee code (legacy L164–218). */
employeesRouter.get("/employees/options", requirePermission("view_employees"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const [depts, desigs, roleOpts, addresses, lastCode] = await Promise.all([
    db.select({ id: teams.id, name: teams.teamName }).from(teams).where(eq(teams.companyId, companyId)).orderBy(asc(teams.teamName)),
    db.select({ id: designations.id, name: designations.name }).from(designations).where(eq(designations.companyId, companyId)).orderBy(asc(designations.name)),
    db.select({ id: roles.id, name: roles.name, label: roles.displayName }).from(roles).where(eq(roles.companyId, companyId)).orderBy(roles.id),
    db.select({ id: companyAddresses.id, address: companyAddresses.address, isDefault: companyAddresses.isDefault }).from(companyAddresses).where(eq(companyAddresses.companyId, companyId)).orderBy(desc(companyAddresses.isDefault)),
    db.select({ code: employeeDetails.employeeId }).from(employeeDetails).where(and(eq(employeeDetails.companyId, companyId), sql`${employeeDetails.employeeId} is not null`)).orderBy(desc(employeeDetails.id)).limit(1),
  ]);
  // next code = trailing-number of the last code + 1, zero-padded (legacy L212–216)
  let nextEmployeeId: string | null = lastCode[0]?.code ?? null;
  const m = nextEmployeeId?.match(/^(.*?)(\d+)$/);
  if (m) nextEmployeeId = (m[1] ?? "") + String(Number(m[2]) + 1).padStart((m[2] ?? "").length, "0");

  res.json({
    departments: depts,
    designations: desigs,
    roles: roleOpts.map((r) => ({ id: r.id, name: r.name, label: r.label || r.name })),
    companyAddresses: addresses,
    defaultCompanyAddressId: addresses.find((a) => a.isDefault)?.id ?? addresses[0]?.id ?? null,
    nextEmployeeId,
  });
});

/** GET /api/employees/export — filtered CSV stream (legacy L576–619). */
employeesRouter.get("/employees/export", requirePermission("view_employees"), async (req, res) => {
  const { companyId, userId } = { companyId: req.auth!.companyId, userId: req.auth!.userId };
  const f = listQuery.parse(req.query);
  const conds = baseConditions(companyId, userId, req.scope!);
  if (f.search) {
    const t = `%${f.search}%`;
    conds.push(or(like(users.name, t), like(users.email, t))!);
  }
  if (f.department_id > 0) conds.push(eq(employeeDetails.departmentId, f.department_id));
  if (f.status === "active") conds.push(eq(users.status, "active"));
  else if (f.status === "inactive") conds.push(eq(users.status, "deactive"));

  const rows = await fromEmployees(companyId)
    .where(and(...conds))
    .orderBy(asc(users.name));

  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const ddmmyyyy = (d: unknown) => {
    if (!d) return "";
    const dt = new Date(d as string);
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  };
  const header = ["ID", "Name", "Email", "Code", "Department", "Designation", "Status", "Joined"];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.id,
        r.name ?? "",
        r.email ?? "",
        r.employeeCode ?? "",
        r.departmentName ?? "",
        r.designationName ?? "",
        r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : "",
        ddmmyyyy(r.createdAt),
      ]
        .map(esc)
        .join(","),
    );
  }
  const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
  res.setHeader("Content-Type", "text/csv; charset=UTF-8");
  res.setHeader("Content-Disposition", `attachment; filename="employees_${stamp}.csv"`);
  res.send(lines.join("\n"));
});

const bulkBody = z.object({
  ids: z.array(z.coerce.number().int()).min(1),
  action: z.literal("archive"),
});

/** POST /api/employees/bulk — archive (status=deactive). Gate delete_employees=ALL (legacy L557–571). */
employeesRouter.post("/employees/bulk", requirePermission("delete_employees"), async (req, res) => {
  if (req.scope !== SCOPE.ALL) return res.status(403).json({ error: "Forbidden", permission: "delete_employees" });
  const parsed = bulkBody.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const ids = parsed.data.ids.filter((id) => id !== req.auth!.userId); // never archive self
  if (!ids.length) return res.json({ ok: true, message: "0 employee(s) archived" });
  await db
    .update(users)
    .set({ status: "deactive" })
    .where(and(eq(users.companyId, req.auth!.companyId), inArray(users.id, ids)));
  res.json({ ok: true, message: `${ids.length} employee(s) archived` });
});

/* ────────────────────────── Detail drawer (legacy detail() L466–549) ──────────────────────────
 * Detail loads via withoutGlobalScope(ActiveScope) so deactivated employees still open (L474), and
 * drops the employee-role filter so admin/HR/QA rows resolve too (L471–476). Tiles are fetch-on-open
 * (our restructure) and defensive — a missing relation never breaks the drawer. Company-scoped. */

/** GET /api/employees/:id — Profile tab (user + employment + reporting line + emergency contacts + skills). */
employeesRouter.get("/employees/:id", requirePermission("view_employees"), async (req, res) => {
  const id = Number(req.params.id);
  const companyId = req.auth!.companyId;
  if (!Number.isInteger(id)) return res.status(400).json({ error: "Bad id" });
  // Record-scope guard: OWNED/ADDED roles may only open their own record (employee owner = self).
  if (req.scope !== SCOPE.ALL && req.scope !== SCOPE.BOTH && id !== req.auth!.userId) return res.status(403).json({ error: "Forbidden" });

  const [row] = await db
    .select({
      id: users.id,
      name: users.name,
      email: users.email,
      image: users.image,
      mobile: users.mobile,
      gender: users.gender,
      status: users.status,
      createdAt: users.createdAt,
      employeeCode: employeeDetails.employeeId,
      address: employeeDetails.address,
      aboutMe: employeeDetails.aboutMe,
      dob: employeeDetails.dateOfBirth,
      joiningDate: employeeDetails.joiningDate,
      lastDate: employeeDetails.lastDate,
      employmentType: employeeDetails.employmentType,
      probationEndDate: employeeDetails.probationEndDate,
      contractEndDate: employeeDetails.contractEndDate,
      noticePeriodStartDate: employeeDetails.noticePeriodStartDate,
      noticePeriodEndDate: employeeDetails.noticePeriodEndDate,
      departmentName: teams.teamName,
      designationName: designations.name,
      reportingToId: employeeDetails.reportingTo,
    })
    .from(users)
    .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .leftJoin(teams, eq(teams.id, employeeDetails.departmentId))
    .leftJoin(designations, eq(designations.id, employeeDetails.designationId))
    .where(and(eq(users.id, id), eq(users.companyId, companyId)))
    .limit(1);
  if (!row) return res.status(404).json({ error: "Employee not found" });

  let reportingTo: { id: number; name: string } | null = null;
  if (row.reportingToId) {
    const [m] = await db.select({ id: users.id, name: users.name }).from(users).where(eq(users.id, row.reportingToId)).limit(1);
    reportingTo = m ?? null;
  }
  const roleRows = await db
    .select({ name: roles.name, displayName: roles.displayName })
    .from(roleUser)
    .innerJoin(roles, eq(roles.id, roleUser.roleId))
    .where(eq(roleUser.userId, id));
  const contacts = await db
    .select({ id: emergencyContacts.id, name: emergencyContacts.name, relation: emergencyContacts.relation, mobile: emergencyContacts.mobile })
    .from(emergencyContacts)
    .where(eq(emergencyContacts.userId, id));
  const skillRows = await db
    .select({ name: skills.name })
    .from(employeeSkills)
    .innerJoin(skills, eq(skills.id, employeeSkills.skillId))
    .where(eq(employeeSkills.userId, id));

  res.json({ ...row, reportingTo, roles: roleRows, emergencyContacts: contacts, skills: skillRows.map((s) => s.name) });
});

/** GET /api/employees/:id/attendance-summary — current-month present / avg hours / late (legacy L484–504). */
employeesRouter.get("/employees/:id/attendance-summary", requirePermission("view_employees"), async (req, res) => {
  const id = Number(req.params.id);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01 00:00:00`;
  const rows = await db
    .select({ clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime, late: attendances.late })
    .from(attendances)
    .where(and(eq(attendances.userId, id), sql`${attendances.clockInTime} >= ${monthStart}`));
  let present = 0;
  let late = 0;
  let totalMin = 0;
  let closed = 0;
  const seen = new Set<string>();
  for (const a of rows) {
    const day = new Date(a.clockIn as unknown as string).toISOString().slice(0, 10);
    if (!seen.has(day)) {
      seen.add(day);
      present++;
      if (a.late === "yes") late++;
    }
    if (a.clockOut) {
      totalMin += (new Date(a.clockOut as unknown as string).getTime() - new Date(a.clockIn as unknown as string).getTime()) / 60000;
      closed++;
    }
  }
  res.json({ present, late, avgHours: closed ? Math.round((totalMin / closed / 60) * 10) / 10 : 0 });
});

/** GET /api/employees/:id/leave-balances — per-type total / used / remaining (legacy L507–522). */
employeesRouter.get("/employees/:id/leave-balances", requirePermission("view_employees"), async (req, res) => {
  const id = Number(req.params.id);
  const year = new Date().getFullYear();
  const quotas = await db
    .select({ leaveTypeId: employeeLeaveQuotas.leaveTypeId, total: employeeLeaveQuotas.noOfLeaves, name: leaveTypes.typeName })
    .from(employeeLeaveQuotas)
    .leftJoin(leaveTypes, eq(leaveTypes.id, employeeLeaveQuotas.leaveTypeId))
    .where(eq(employeeLeaveQuotas.userId, id));
  const out = await Promise.all(
    quotas.map(async (q) => {
      const [u] = await db
        .select({ used: sql<number>`COALESCE(SUM(CASE WHEN ${leaves.duration}='half day' THEN 0.5 ELSE 1 END),0)` })
        .from(leaves)
        .where(and(eq(leaves.userId, id), eq(leaves.leaveTypeId, q.leaveTypeId), eq(leaves.status, "approved"), sql`YEAR(${leaves.leaveDate})=${year}`));
      const total = Number(q.total ?? 0);
      const used = Number(u?.used ?? 0);
      return { name: q.name ?? "Leave", total, used, remaining: Math.max(0, total - used) };
    }),
  );
  res.json({ balances: out });
});

/** GET /api/employees/:id/timelogs-week — this-week minutes + distinct projects (legacy L524–538). */
employeesRouter.get("/employees/:id/timelogs-week", requirePermission("view_employees"), async (req, res) => {
  const id = Number(req.params.id);
  const now = new Date();
  const day = (now.getUTCDay() + 6) % 7;
  const weekStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - day));
  const [agg] = await db
    .select({
      minutes: sql<number>`COALESCE(SUM(CAST(${projectTimeLogs.totalMinutes} AS UNSIGNED)),0)`,
      projects: sql<number>`COUNT(DISTINCT ${projectTimeLogs.projectId})`,
    })
    .from(projectTimeLogs)
    .where(and(eq(projectTimeLogs.userId, id), sql`${projectTimeLogs.startTime} >= ${weekStart}`));
  res.json({ minutes: Number(agg?.minutes ?? 0), projects: Number(agg?.projects ?? 0) });
});

/* ────────────────────── New / Edit drawer (legacy store L256–269 → native; edit L342–433) ──────
 * Validation mirrors Admin\Employee\StoreRequest (employee_id+email unique per company; required
 * name/password/joining/department/designation). Credentials are created via Better Auth so the new
 * hire can sign in; company-scope + employee role + chosen role + permission-sync applied. Dates
 * arrive ISO from the client (the picker submits yyyy-mm-dd). Side-effects deferred + flagged:
 * welcome/invite email (no SMTP yet), salary cycle (B5). */

const d = (s?: string | null) => (s ? new Date(s) : null);

const employeeBody = z.object({
  employee_id: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(50),
  email: z.string().email().max(100),
  password: z.string().min(8).max(50).optional(),
  department_id: z.coerce.number().int().positive(),
  designation_id: z.coerce.number().int().positive(),
  role_id: z.coerce.number().int().positive().optional(),
  joining_date: z.string().min(1),
  mobile: z.string().max(20).optional(),
  gender: z.enum(["male", "female", "others"]).optional(),
  date_of_birth: z.string().optional(),
  marital_status: z.string().optional(),
  employment_type: z.string().optional(),
  hourly_rate: z.coerce.number().nonnegative().optional(),
  slack_username: z.string().max(30).optional(),
  company_address_id: z.coerce.number().int().positive().optional(),
  reporting_to: z.coerce.number().int().positive().optional(),
  probation_end_date: z.string().optional(),
  contract_end_date: z.string().optional(),
  internship_end_date: z.string().optional(),
  notice_period_start_date: z.string().optional(),
  notice_period_end_date: z.string().optional(),
  address: z.string().optional(),
  about_me: z.string().optional(),
  status: z.enum(["active", "deactive"]).default("active"),
});

/** POST /api/employees — create (gate add_employees=ALL, legacy L258). */
employeesRouter.post("/employees", requirePermission("add_employees"), async (req, res) => {
  if (req.scope !== SCOPE.ALL) return res.status(403).json({ error: "Forbidden", permission: "add_employees" });
  const companyId = req.auth!.companyId;
  const parsed = employeeBody.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const b = parsed.data;
  if (!b.password) return res.status(422).json({ error: "Password is required", details: { fieldErrors: { password: ["Required"] } } });

  // Uniqueness within company (StoreRequest L31, L33).
  const dupEmail = await db.select({ id: users.id }).from(users).where(and(eq(users.companyId, companyId), eq(users.email, b.email))).limit(1);
  if (dupEmail[0]) return res.status(422).json({ error: "Email already in use", details: { fieldErrors: { email: ["Already in use"] } } });
  const dupCode = await db.select({ id: employeeDetails.id }).from(employeeDetails).where(and(eq(employeeDetails.companyId, companyId), eq(employeeDetails.employeeId, b.employee_id))).limit(1);
  if (dupCode[0]) return res.status(422).json({ error: "Employee ID already in use", details: { fieldErrors: { employee_id: ["Already in use"] } } });

  const [employeeRole] = await db.select({ id: roles.id }).from(roles).where(and(eq(roles.companyId, companyId), eq(roles.name, "employee"))).limit(1);
  if (!employeeRole) return res.status(500).json({ error: "No 'employee' role for company" });

  // Credentials via Better Auth (so the new hire can sign in).
  try {
    await auth.api.signUpEmail({ body: { email: b.email, password: b.password, name: b.name } });
  } catch (e) {
    return res.status(500).json({ error: "Could not create login: " + (e as Error).message });
  }
  const [created] = await db.select({ id: users.id }).from(users).where(eq(users.email, b.email)).limit(1);
  if (!created) return res.status(500).json({ error: "User was not created" });
  const userId = created.id;

  await db.update(users).set({ companyId, mobile: b.mobile ?? null, gender: b.gender ?? null, status: b.status }).where(eq(users.id, userId));
  await db.insert(employeeDetails).values({
    companyId,
    userId,
    employeeId: b.employee_id,
    departmentId: b.department_id,
    designationId: b.designation_id,
    joiningDate: new Date(b.joining_date),
    dateOfBirth: d(b.date_of_birth),
    maritalStatus: b.marital_status || "single",
    employmentType: b.employment_type ?? null,
    hourlyRate: b.hourly_rate ?? null,
    slackUsername: b.slack_username || null,
    companyAddressId: b.company_address_id ?? null,
    reportingTo: b.reporting_to ?? null,
    probationEndDate: d(b.probation_end_date),
    contractEndDate: d(b.contract_end_date),
    internshipEndDate: d(b.internship_end_date),
    noticePeriodStartDate: d(b.notice_period_start_date),
    noticePeriodEndDate: d(b.notice_period_end_date),
    address: b.address ?? null,
    aboutMe: b.about_me ?? null,
    addedBy: req.auth!.userId,
  });
  await db.insert(roleUser).values({ userId, roleId: employeeRole.id });
  if (b.role_id && b.role_id !== employeeRole.id) await db.insert(roleUser).values({ userId, roleId: b.role_id });
  await syncUserPermissions(userId);
  res.status(201).json({ id: userId, message: "Employee added" });
});

/** GET /api/employees/:id/edit — editable-field prefill (legacy L342–396). */
employeesRouter.get("/employees/:id/edit", requirePermission("view_employees"), async (req, res) => {
  const id = Number(req.params.id);
  const companyId = req.auth!.companyId;
  const [u] = await db
    .select({
      id: users.id, name: users.name, email: users.email, mobile: users.mobile, gender: users.gender, status: users.status,
      employee_id: employeeDetails.employeeId, department_id: employeeDetails.departmentId, designation_id: employeeDetails.designationId,
      joining_date: employeeDetails.joiningDate, date_of_birth: employeeDetails.dateOfBirth, marital_status: employeeDetails.maritalStatus,
      employment_type: employeeDetails.employmentType, hourly_rate: employeeDetails.hourlyRate, slack_username: employeeDetails.slackUsername,
      company_address_id: employeeDetails.companyAddressId, reporting_to: employeeDetails.reportingTo, address: employeeDetails.address, about_me: employeeDetails.aboutMe,
      probation_end_date: employeeDetails.probationEndDate, contract_end_date: employeeDetails.contractEndDate,
      internship_end_date: employeeDetails.internshipEndDate, notice_period_start_date: employeeDetails.noticePeriodStartDate, notice_period_end_date: employeeDetails.noticePeriodEndDate,
    })
    .from(users)
    .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .where(and(eq(users.id, id), eq(users.companyId, companyId)))
    .limit(1);
  if (!u) return res.status(404).json({ error: "Employee not found" });
  const [firstRole] = await db.select({ id: roleUser.roleId }).from(roleUser).innerJoin(roles, eq(roles.id, roleUser.roleId)).where(and(eq(roleUser.userId, id), ne(roles.name, "employee"))).limit(1);
  res.json({ ...u, role_id: firstRole?.id ?? null });
});

/** POST /api/employees/:id — update (gate edit_employees ∈ {all,added,both}, legacy L406). */
employeesRouter.post("/employees/:id", requirePermission("edit_employees"), async (req, res) => {
  if (![SCOPE.ALL, SCOPE.ADDED, SCOPE.BOTH].includes(req.scope as 1 | 3 | 4)) return res.status(403).json({ error: "Forbidden" });
  const id = Number(req.params.id);
  const companyId = req.auth!.companyId;
  const parsed = employeeBody.partial().safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const b = parsed.data;

  const [exists] = await db.select({ id: users.id }).from(users).where(and(eq(users.id, id), eq(users.companyId, companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Employee not found" });
  // email unique within company excluding self
  if (b.email) {
    const dup = await db.select({ id: users.id }).from(users).where(and(eq(users.companyId, companyId), eq(users.email, b.email), ne(users.id, id))).limit(1);
    if (dup[0]) return res.status(422).json({ error: "Email already in use", details: { fieldErrors: { email: ["Already in use"] } } });
  }
  await db.update(users).set({
    ...(b.name ? { name: b.name } : {}),
    ...(b.email ? { email: b.email } : {}),
    mobile: b.mobile ?? null,
    gender: b.gender ?? null,
    ...(b.status ? { status: b.status } : {}),
  }).where(eq(users.id, id));
  await db.update(employeeDetails).set({
    ...(b.employee_id ? { employeeId: b.employee_id } : {}),
    ...(b.department_id ? { departmentId: b.department_id } : {}),
    ...(b.designation_id ? { designationId: b.designation_id } : {}),
    ...(b.joining_date ? { joiningDate: new Date(b.joining_date) } : {}),
    dateOfBirth: d(b.date_of_birth),
    ...(b.marital_status ? { maritalStatus: b.marital_status } : {}),
    employmentType: b.employment_type ?? null,
    hourlyRate: b.hourly_rate ?? null,
    slackUsername: b.slack_username || null,
    companyAddressId: b.company_address_id ?? null,
    reportingTo: b.reporting_to ?? null,
    probationEndDate: d(b.probation_end_date),
    contractEndDate: d(b.contract_end_date),
    internshipEndDate: d(b.internship_end_date),
    noticePeriodStartDate: d(b.notice_period_start_date),
    noticePeriodEndDate: d(b.notice_period_end_date),
    address: b.address ?? null,
    aboutMe: b.about_me ?? null,
    lastUpdatedBy: req.auth!.userId,
  }).where(eq(employeeDetails.userId, id));
  res.json({ id, message: "Employee updated" });
});

/** DELETE /api/employees/:id — FK-aware: archive if the employee has business history, else hard-delete
 *  (gate delete_employees=ALL; preserves audit trail, legacy L301–333). */
employeesRouter.delete("/employees/:id", requirePermission("delete_employees"), async (req, res) => {
  if (req.scope !== SCOPE.ALL) return res.status(403).json({ error: "Forbidden", permission: "delete_employees" });
  const id = Number(req.params.id);
  const companyId = req.auth!.companyId;
  if (id === req.auth!.userId) return res.status(422).json({ error: "You can't delete your own account" });
  const [exists] = await db.select({ id: users.id }).from(users).where(and(eq(users.id, id), eq(users.companyId, companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Employee not found" });

  // History check — attendance / leaves / time logs. If any exist, never hard-delete (preserve audit).
  const [att] = await db.select({ c: count() }).from(attendances).where(eq(attendances.userId, id));
  const [lv] = await db.select({ c: count() }).from(leaves).where(eq(leaves.userId, id));
  const [tl] = await db.select({ c: count() }).from(projectTimeLogs).where(eq(projectTimeLogs.userId, id));
  const hasHistory = Number(att?.c ?? 0) + Number(lv?.c ?? 0) + Number(tl?.c ?? 0) > 0;
  if (hasHistory) {
    await db.update(users).set({ status: "deactive" }).where(eq(users.id, id));
    return res.json({ archived: true, message: "Employee has records — archived instead of deleted" });
  }
  await db.delete(users).where(eq(users.id, id)); // cascades employee_details + role_user
  res.json({ deleted: true, message: "Employee deleted" });
});
